import {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
  useEffect,
} from 'react'

import * as v from 'valibot'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { DefaultApps } from '@tribeplatform/react-components/Apps'
import { useFirstRenderContext } from '@tribeplatform/react-components/common/ssr'
import { TribeEmitterEvent } from '@tribeplatform/react-sdk'
import {
  useApp,
  useAppNetworkSettings,
  useBMListener,
  useNetwork,
} from '@tribeplatform/react-sdk/hooks'
import { CookieConsent } from '@tribeplatform/react-sdk/types'

import { cookieConsentAppSettingsSchema } from './types.js'

export type CookieConsentContextType = {
  cookieConsent: CookieConsent | undefined
  isCookieAppInstalled: boolean
  isAwaitingConsent: boolean
  setCookieConsent: (preferences: CookieConsent['permissions']) => void
}

export const CookieConsentContext =
  createContext<CookieConsentContextType | null>(null)

export const CookieConsentProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { isFirstRender } = useFirstRenderContext()
  const [currentCookieConsent, setCurrentCookieConsent] = useState<
    CookieConsent | undefined
  >()
  const [isConsentLoaded, setIsConsentLoaded] = useState(false)
  const { data: network } = useNetwork()
  const [hasFetchAppAccess] = hasScopesPermission(network, ['app'])

  useEffect(() => {
    setCurrentCookieConsent(window.BM.cookieManager?.getCookieConsent())
    setIsConsentLoaded(true)
  }, [])

  const { data: cookieConsentApp, isInitialLoading } = useApp({
    variables: {
      slug: DefaultApps.CookieConsentManager,
    },
    useQueryOptions: {
      enabled: !isFirstRender && hasFetchAppAccess,
    },
  })

  const { data: appSettingsString, isLoading: isAppSettingsLoading } =
    useAppNetworkSettings({
      variables: {
        appId: cookieConsentApp?.id,
      },
      useQueryOptions: {
        enabled: !!cookieConsentApp?.id,
      },
    })

  const appSettings = useMemo(() => {
    const schema = v.pipe(
      v.string(),
      v.parseJson(),
      cookieConsentAppSettingsSchema,
    )

    const result = v.safeParse(schema, appSettingsString)
    if (!result.success) {
      return undefined
    }

    return result.output
  }, [appSettingsString])

  const isCookieAppInstalled = Boolean(
    !isInitialLoading && cookieConsentApp?.installed,
  )

  const hasVersionMismatch =
    !isAppSettingsLoading &&
    currentCookieConsent?.version !== appSettings?.version

  const isAwaitingConsent = Boolean(
    isCookieAppInstalled &&
      isConsentLoaded &&
      (!currentCookieConsent?.prompted || hasVersionMismatch),
  )

  const setCookieConsent = useCallback(
    (preferences: CookieConsent['permissions']) => {
      const consent: CookieConsent = {
        ...currentCookieConsent,
        prompted: true,
        datestamp: new Date().toISOString(),
        version: appSettings?.version,
        permissions: preferences,
      }
      window.BM.cookieManager.setCookieConsent(consent)
      setCurrentCookieConsent(consent)
    },
    [currentCookieConsent, appSettings?.version],
  )

  const handleCookieConsentChange = useCallback(
    ({ cookieConsent }: { cookieConsent: CookieConsent }) =>
      setCurrentCookieConsent(cookieConsent),
    [setCurrentCookieConsent],
  )

  useBMListener(
    TribeEmitterEvent.ON_COOKIE_CONSENT_CHANGE,
    handleCookieConsentChange,
  )

  const value = useMemo(() => {
    return {
      cookieConsent: currentCookieConsent,
      setCookieConsent,
      isCookieAppInstalled,
      isAwaitingConsent,
    }
  }, [
    currentCookieConsent,
    setCookieConsent,
    isCookieAppInstalled,
    isAwaitingConsent,
  ])

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext)

  if (!context) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider',
    )
  }

  return context
}
